import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  CreateFiledDocumentPayload,
  FiledDocument,
  FiledDocumentFilters,
  PaginatedResponse,
} from '@/lib/types';

const FILED_DOCUMENTS_API = '/api/v1/filed-documents';

function cleanFilters(filters: FiledDocumentFilters): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

function buildFormData(payload: CreateFiledDocumentPayload): FormData {
  const formData = new FormData();
  formData.append('title', payload.title);
  if (payload.category) formData.append('category', payload.category);
  if (payload.description) formData.append('description', payload.description);
  if (payload.expiry_date) formData.append('expiry_date', payload.expiry_date);
  if (payload.attached_type) formData.append('attached_type', payload.attached_type);
  if (payload.attached_id !== undefined && payload.attached_id !== null) {
    formData.append('attached_id', String(payload.attached_id));
  }
  formData.append('file', payload.file);
  return formData;
}

export function useFiledDocuments(filters: FiledDocumentFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);

  return useQuery({
    queryKey: queryKeys.filedDocuments.list(clean),
    queryFn: async (): Promise<PaginatedResponse<FiledDocument>> => {
      const response = await apiClient.get<PaginatedResponse<FiledDocument>>(
        `${FILED_DOCUMENTS_API}/`,
        { params: clean }
      );
      return response.data;
    },
    enabled,
  });
}

export function useCreateFiledDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateFiledDocumentPayload): Promise<FiledDocument> => {
      const response = await apiClient.post<FiledDocument>(
        `${FILED_DOCUMENTS_API}/`,
        buildFormData(payload),
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.filedDocuments.all });
    },
  });
}
